import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthContext';
import { apiClient, apiFetch, fileUrl } from '@/lib/api';
import { downscaleImage } from '@/lib/image';
import { generatePassword } from '@/lib/password';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PhoneInput } from '@/components/PhoneInput';

/**
 * ProfilePage — shared by /admin/profile and /portal/profile (owner spec
 * 2026-06-11): profile image, first/last name, email, phone, and a
 * change-password form. fullName is composed server-side.
 */
export default function ProfilePage() {
  const { user, role, refresh } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Prefill; when firstName/lastName were never set, split fullName.
  useEffect(() => {
    if (!user) return;
    const guessFirst = user.fullName.split(/\s+/)[0] ?? '';
    const guessLast = user.fullName.split(/\s+/).slice(1).join(' ');
    setFirstName(user.firstName ?? guessFirst);
    setLastName(user.lastName ?? guessLast);
    setEmail(user.email);
    setPhone(user.phone ?? '');
  }, [user]);

  if (!user) return null;

  const initials =
    `${(user.firstName ?? user.fullName).charAt(0)}${(user.lastName ?? '').charAt(0)}`.toUpperCase() ||
    user.email.slice(0, 2).toUpperCase();

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await apiFetch('/api/auth/me/profile', {
        method: 'PATCH',
        body: { firstName, lastName, email, phone: phone.trim() || null },
      });
      await refresh();
      toast.success('Profile updated');
    } catch (err) {
      const conflict = isAxiosError(err) && err.response?.status === 409;
      toast.error(conflict ? 'That email address is already in use' : "Couldn't save — try again");
    } finally {
      setSavingProfile(false);
    }
  }

  async function uploadAvatar(file: File) {
    setUploadingAvatar(true);
    try {
      const image = await downscaleImage(file);
      await apiClient.put('/api/auth/me/avatar', image, {
        headers: { 'Content-Type': image.type || file.type },
      });
      await refresh();
      toast.success('Profile image updated');
    } catch {
      toast.error('Image must be a PNG, JPEG, or WebP under 2 MB');
    } finally {
      setUploadingAvatar(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < 12) {
      toast.error('New password must be at least 12 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await apiFetch<void>('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const unauthorized = isAxiosError(err) && err.response?.status === 401;
      toast.error(
        unauthorized ? 'Current password is incorrect' : 'Could not update password — try again',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Your profile</h2>
        <p className="text-sm text-muted-foreground">Account details and security.</p>
      </div>

      <section className="rounded-lg border bg-card p-6">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="h-16 w-16">
            {user.avatarPath && <AvatarImage src={fileUrl(user.avatarPath)} alt="" />}
            <AvatarFallback className="bg-primary text-primary-foreground text-lg">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploadingAvatar}
              onClick={() => fileRef.current?.click()}
            >
              {uploadingAvatar ? 'Uploading…' : 'Change photo'}
            </Button>
            <p className="text-xs text-muted-foreground mt-1.5">
              PNG, JPEG, or WebP · up to 2 MB · {role}
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadAvatar(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <form onSubmit={saveProfile} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="first-name">First name</Label>
              <Input
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last-name">Last name</Label>
              <Input
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="profile-email">Email</Label>
              <Input
                id="profile-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-phone">Phone</Label>
              <PhoneInput id="profile-phone" value={phone} onChange={setPhone} />
            </div>
          </div>
          <Button type="submit" disabled={savingProfile}>
            {savingProfile ? 'Saving…' : 'Save profile'}
          </Button>
        </form>
      </section>

      <section className="rounded-lg border bg-card p-6">
        <h3 className="text-lg font-semibold mb-4">Change password</h3>
        <form onSubmit={changePassword} className="space-y-4 max-w-sm">
          <div className="space-y-1.5">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <div className="flex gap-2">
              <Input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={12}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const pw = generatePassword();
                  setNewPassword(pw);
                  setConfirmPassword(pw);
                  setShowNewPassword(true);
                }}
              >
                Generate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              At least 12 characters. Generate fills both fields — save it somewhere safe.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={busy}>
            {busy ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      </section>
    </div>
  );
}
